import * as React from "react";

export type ThemeMode = "light" | "dark" | "system";

interface ThemeContextValue {
  mode: ThemeMode;
  resolvedMode: "light" | "dark";
  setMode: (mode: ThemeMode) => void;
  toggleMode: () => void;
}

const LS_KEY = "app.theme-mode.v1";

const ThemeCtx = React.createContext<ThemeContextValue | undefined>(undefined);

function getSystemPref(): "light" | "dark" {
  if (typeof window !== "undefined" && window.matchMedia) {
    return window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
  }
  return "light";
}

export function ThemeModeProvider({ children }: { children: React.ReactNode }) {
  const [mode, setModeState] = React.useState<ThemeMode>(() => {
    try {
      const v = localStorage.getItem(LS_KEY);
      if (v === "light" || v === "dark" || v === "system") return v;
    } catch {}
    return "dark";
  });

  const [systemPref, setSystemPref] = React.useState<"light" | "dark">(getSystemPref);

  React.useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const h = (e: MediaQueryListEvent) =>
      setSystemPref(e.matches ? "dark" : "light");
    mq.addEventListener("change", h);
    return () => mq.removeEventListener("change", h);
  }, []);

  const setMode = React.useCallback((m: ThemeMode) => {
    setModeState(m);
    try {
      localStorage.setItem(LS_KEY, m);
    } catch {}
  }, []);

  const toggleMode = React.useCallback(() => {
    setModeState((prev) => {
      const next: ThemeMode =
        prev === "light" ? "dark" : prev === "dark" ? "system" : "light";
      try {
        localStorage.setItem(LS_KEY, next);
      } catch {}
      return next;
    });
  }, []);

  const resolvedMode = mode === "system" ? systemPref : mode;

  const value = React.useMemo(
    () => ({ mode, resolvedMode, setMode, toggleMode }),
    [mode, resolvedMode, setMode, toggleMode],
  );

  return <ThemeCtx.Provider value={value}>{children}</ThemeCtx.Provider>;
}

export function useThemeMode() {
  const ctx = React.useContext(ThemeCtx);
  if (!ctx) throw new Error("useThemeMode must be used within <ThemeModeProvider>");
  return ctx;
}
